import {
  OPEN_STATUSES,
  PRIORITY_RANK,
  TASK_STATUSES,
  type Priority,
  type Project,
  type Space,
  type TaskQuery,
  type TaskStatus,
  type TaskWithFlags,
} from "@desk/contracts";

export type SortKey = "priority" | "deadline" | "created" | "manual";
export const SORT_LABEL: Record<SortKey, string> = {
  priority: "Priority",
  deadline: "Deadline",
  created: "Created",
  manual: "Manual",
};

export interface TaskFilters {
  areaId?: string;
  spaceId?: string;
  /** A project id, or "none" for tasks without a project. */
  projectId?: string;
  status?: TaskStatus[];
  priority?: Priority[];
  label?: string;
  q?: string;
  sort: SortKey;
  showCompleted: boolean;
}

export const DEFAULT_FILTERS: TaskFilters = { sort: "priority", showCompleted: false };

const CLOSED: TaskStatus[] = ["done", "cancelled"];

export function isClosed(t: { status: TaskStatus }) {
  return CLOSED.includes(t.status);
}

/** Server query for a filter state. Client-side filtering below re-applies the same rules. */
export function filtersToQuery(f: TaskFilters, base: Partial<TaskQuery> = {}): TaskQuery {
  const q: TaskQuery = { ...base };
  if (f.areaId) q.areaId = f.areaId;
  if (f.spaceId) q.spaceId = f.spaceId;
  if (f.projectId === "none") q.noProject = true;
  else if (f.projectId) q.projectId = f.projectId;
  if (f.status?.length) q.status = f.status;
  if (f.priority?.length) q.priority = f.priority;
  if (f.label) q.label = f.label;
  if (f.q?.trim()) q.q = f.q.trim();
  if (f.showCompleted || f.status?.some((s) => CLOSED.includes(s))) q.includeClosed = true;
  q.includeSnoozed = true;
  q.sort = f.sort;
  return q;
}

export function matchesFilters(t: TaskWithFlags, f: TaskFilters): boolean {
  if (t.deletedAt) return false;
  if (f.areaId && t.areaId !== f.areaId) return false;
  if (f.spaceId && t.spaceId !== f.spaceId) return false;
  if (f.projectId === "none" && t.projectId) return false;
  if (f.projectId && f.projectId !== "none" && t.projectId !== f.projectId) return false;
  if (f.status?.length) {
    if (!f.status.includes(t.status)) return false;
  } else if (!f.showCompleted && isClosed(t)) return false;
  if (f.priority?.length && !f.priority.includes(t.priority)) return false;
  if (f.label && !t.labels.some((l) => l.toLowerCase() === f.label!.toLowerCase())) return false;
  if (f.q?.trim()) {
    const needle = f.q.trim().toLowerCase();
    const hay = `${t.title} ${t.readableId} ${t.description} ${t.labels.join(" ")}`.toLowerCase();
    if (!hay.includes(needle)) return false;
  }
  return true;
}

/** One row per task id, keeping the first occurrence. */
export function dedupe<T extends { id: string }>(tasks: T[]): T[] {
  const seen = new Set<string>();
  return tasks.filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)));
}

const STATUS_RANK: Record<TaskStatus, number> = { in_progress: 0, todo: 1, backlog: 2, done: 3, cancelled: 4 };

export function compareTasks(sort: SortKey) {
  return (a: TaskWithFlags, b: TaskWithFlags): number => {
    const closed = Number(isClosed(a)) - Number(isClosed(b));
    if (closed) return closed;
    switch (sort) {
      case "priority": {
        const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
        if (p) return p;
        const s = STATUS_RANK[a.status] - STATUS_RANK[b.status];
        if (s) return s;
        return a.position - b.position || a.createdAt.localeCompare(b.createdAt);
      }
      case "deadline": {
        if (a.deadline !== b.deadline) {
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return a.deadline.localeCompare(b.deadline);
        }
        return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || a.createdAt.localeCompare(b.createdAt);
      }
      case "created":
        return b.createdAt.localeCompare(a.createdAt);
      case "manual":
        return a.position - b.position || a.createdAt.localeCompare(b.createdAt);
    }
  };
}

export function sortTasks<T extends TaskWithFlags>(tasks: T[], sort: SortKey): T[] {
  return [...tasks].sort(compareTasks(sort));
}

/** Today's planned order in priority mode: Urgent → No priority, In progress pinned, stable within ties. */
export function sortPlanned<T extends { task: TaskWithFlags }>(rows: T[], mode: "priority" | "manual"): T[] {
  if (mode === "manual") return rows;
  const index = new Map(rows.map((r, i) => [r.task.id, i]));
  return [...rows].sort((a, b) => {
    const ip = Number(b.task.status === "in_progress") - Number(a.task.status === "in_progress");
    if (ip) return ip;
    return PRIORITY_RANK[a.task.priority] - PRIORITY_RANK[b.task.priority] || index.get(a.task.id)! - index.get(b.task.id)!;
  });
}

/** Clear child filters that no longer belong to their parent. */
export function constrainFilters(f: TaskFilters, spaces: Space[], projects: Project[]): TaskFilters {
  const next = { ...f };
  if (next.spaceId) {
    const s = spaces.find((x) => x.id === next.spaceId);
    if (!s || (next.areaId && s.areaId !== next.areaId)) delete next.spaceId;
  }
  if (next.projectId && next.projectId !== "none") {
    const p = projects.find((x) => x.id === next.projectId);
    if (!p || (next.spaceId && p.spaceId !== next.spaceId) || (next.areaId && p.areaId !== next.areaId)) delete next.projectId;
  }
  return next;
}

export function filtersFromParams(p: URLSearchParams, defaults: TaskFilters = DEFAULT_FILTERS): TaskFilters {
  const list = <T extends string>(key: string, allowed: readonly T[]) => {
    const v = p.get(key);
    if (!v) return undefined;
    const items = v.split(",").filter((x): x is T => (allowed as readonly string[]).includes(x));
    return items.length ? items : undefined;
  };
  const sort = p.get("sort");
  return {
    areaId: p.get("area") ?? defaults.areaId,
    spaceId: p.get("space") ?? defaults.spaceId,
    projectId: p.get("project") ?? defaults.projectId,
    status: list("status", TASK_STATUSES),
    priority: list("priority", ["none", "low", "medium", "high", "urgent"] as const),
    label: p.get("label") ?? undefined,
    q: p.get("q") ?? undefined,
    sort: sort && sort in SORT_LABEL ? (sort as SortKey) : defaults.sort,
    showCompleted: p.get("completed") === "1" || defaults.showCompleted,
  };
}

export function filtersToParams(f: TaskFilters, defaults: TaskFilters = DEFAULT_FILTERS): Record<string, string | undefined> {
  return {
    area: f.areaId !== defaults.areaId ? f.areaId : undefined,
    space: f.spaceId !== defaults.spaceId ? f.spaceId : undefined,
    project: f.projectId !== defaults.projectId ? f.projectId : undefined,
    status: f.status?.length ? f.status.join(",") : undefined,
    priority: f.priority?.length ? f.priority.join(",") : undefined,
    label: f.label || undefined,
    q: f.q || undefined,
    sort: f.sort !== defaults.sort ? f.sort : undefined,
    completed: f.showCompleted && !defaults.showCompleted ? "1" : undefined,
  };
}

export function activeFilterCount(f: TaskFilters, defaults: TaskFilters = DEFAULT_FILTERS): number {
  let n = 0;
  if (f.areaId && f.areaId !== defaults.areaId) n++;
  if (f.spaceId && f.spaceId !== defaults.spaceId) n++;
  if (f.projectId && f.projectId !== defaults.projectId) n++;
  if (f.status?.length) n++;
  if (f.priority?.length) n++;
  if (f.label) n++;
  if (f.q) n++;
  return n;
}

export function isOpen(t: { status: TaskStatus }) {
  return OPEN_STATUSES.includes(t.status);
}
